import {
  DocPackBuildInputError,
  DocPackError,
  type DocPackGeometry,
  type DocPackGeometryRecord,
  type DocPackPageWord,
  type DocPackRect,
} from './types';

// Direction split: encode/build helpers (byteToBase64, writeVarUint) validate
// caller-supplied records and throw the build-side DocPackBuildInputError.
// decode helpers (base64ToBytes, readVarUint) parse a STORED geometry stream on
// the reader path, so their failures are read-side corruption — DocPackError
// with code 'invalid-geometry', matching the rest of read.ts's sync throws.

const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const BASE64_LOOKUP = new Map(
  BASE64_ALPHABET.split('').map((char, index) => [char, index]),
);

const DEFAULT_GEOMETRY_PRECISION = 100;

const byteToBase64 = (value: number) => {
  const char = BASE64_ALPHABET[value];
  if (char === undefined)
    throw DocPackBuildInputError.make({
      message: `Invalid base64 byte ${value}`,
    });
  return char;
};

const bytesToBase64 = (bytes: Uint8Array) => {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const triple = a * 65536 + b * 256 + c;

    out += byteToBase64(Math.floor(triple / 262144) % 64);
    out += byteToBase64(Math.floor(triple / 4096) % 64);
    out +=
      i + 1 < bytes.length ? byteToBase64(Math.floor(triple / 64) % 64) : '=';
    out += i + 2 < bytes.length ? byteToBase64(triple % 64) : '=';
  }
  return out;
};

const base64ToBytes = (value: string) => {
  const cleaned = value.replaceAll(/\s/g, '');
  if (cleaned.length % 4 !== 0) {
    throw DocPackError.make({
      code: 'invalid-geometry',
      message: 'Invalid base64 geometry length',
    });
  }

  const bytes: number[] = [];
  for (let i = 0; i < cleaned.length; i += 4) {
    const c1 = cleaned[i];
    const c2 = cleaned[i + 1];
    const c3 = cleaned[i + 2];
    const c4 = cleaned[i + 3];
    if (!c1 || !c2 || !c3 || !c4)
      throw DocPackError.make({
        code: 'invalid-geometry',
        message: 'Invalid base64 geometry',
      });

    const n1 = BASE64_LOOKUP.get(c1);
    const n2 = BASE64_LOOKUP.get(c2);
    const n3 = c3 === '=' ? 0 : BASE64_LOOKUP.get(c3);
    const n4 = c4 === '=' ? 0 : BASE64_LOOKUP.get(c4);
    if (
      n1 === undefined ||
      n2 === undefined ||
      n3 === undefined ||
      n4 === undefined
    ) {
      throw DocPackError.make({
        code: 'invalid-geometry',
        message: 'Invalid base64 geometry character',
      });
    }

    const triple = n1 * 262144 + n2 * 4096 + n3 * 64 + n4;
    bytes.push(Math.floor(triple / 65536) % 256);
    if (c3 !== '=') bytes.push(Math.floor(triple / 256) % 256);
    if (c4 !== '=') bytes.push(triple % 256);
  }
  return new Uint8Array(bytes);
};

const writeVarUint = (out: number[], value: number) => {
  if (!Number.isInteger(value) || value < 0) {
    throw DocPackBuildInputError.make({
      message: `Invalid varuint ${value.toString()}`,
    });
  }
  let current = value;
  while (current >= 128) {
    out.push((current % 128) + 128);
    current = Math.floor(current / 128);
  }
  out.push(current);
};

const readVarUint = (bytes: Uint8Array, cursor: { value: number }) => {
  let value = 0;
  let shift = 0;
  while (true) {
    const byte = bytes[cursor.value];
    if (byte === undefined)
      throw DocPackError.make({
        code: 'invalid-geometry',
        message: 'Truncated geometry varuint',
      });
    // Guard BEFORE accumulating: once `value + (byte % 128) * 2 ** shift` can
    // exceed Number.MAX_SAFE_INTEGER, the addition would silently round and the
    // decoded value would be wrong. Throwing here keeps the result exact for
    // every value writeVarUint could have produced (all <= MAX_SAFE_INTEGER).
    const addend = (byte % 128) * 2 ** shift;
    if (value + addend > Number.MAX_SAFE_INTEGER) {
      throw DocPackError.make({
        code: 'invalid-geometry',
        message: 'Geometry varuint is too large',
      });
    }
    cursor.value++;
    value += addend;
    if (byte < 128) return value;
    shift += 7;
  }
};

const zigZagEncode = (value: number) =>
  value >= 0 ? value * 2 : Math.abs(value) * 2 - 1;

const zigZagDecode = (value: number) =>
  value % 2 === 0 ? value / 2 : -(value + 1) / 2;

const quantizeRect = (rect: DocPackRect, precision: number) =>
  rect.map((value) => Math.round(value * precision)) as [
    number,
    number,
    number,
    number,
  ];

export function encodeDocPackGeometry(
  records: readonly DocPackGeometryRecord[],
  precision = DEFAULT_GEOMETRY_PRECISION,
): DocPackGeometry {
  const out: number[] = [];
  writeVarUint(out, records.length);

  let previousCharStart = 0;
  let previousRect: [number, number, number, number] = [0, 0, 0, 0];

  for (const record of records) {
    const rect = quantizeRect(record.rect, precision);
    writeVarUint(out, record.chars[0] - previousCharStart);
    writeVarUint(out, record.chars[1] - record.chars[0]);
    writeVarUint(out, zigZagEncode(rect[0] - previousRect[0]));
    writeVarUint(out, zigZagEncode(rect[1] - previousRect[1]));
    writeVarUint(out, zigZagEncode(rect[2] - previousRect[2]));
    writeVarUint(out, zigZagEncode(rect[3] - previousRect[3]));
    previousCharStart = record.chars[0];
    previousRect = rect;
  }

  return {
    encoding: 'word-rect-delta-v1',
    precision,
    data: bytesToBase64(new Uint8Array(out)),
  };
}

export function encodeDocPackWordGeometry(
  words: readonly DocPackPageWord[],
  precision = DEFAULT_GEOMETRY_PRECISION,
) {
  return encodeDocPackGeometry(
    words.flatMap((word) =>
      word.rects.map((rect) => ({
        chars: word.chars,
        rect,
      })),
    ),
    precision,
  );
}

export function decodeDocPackGeometry(
  geometry: DocPackGeometry,
): readonly DocPackGeometryRecord[] {
  const bytes = base64ToBytes(geometry.data);
  const cursor = { value: 0 };
  const count = readVarUint(bytes, cursor);
  const records: DocPackGeometryRecord[] = [];
  let previousCharStart = 0;
  let previousRect: [number, number, number, number] = [0, 0, 0, 0];

  for (let i = 0; i < count; i++) {
    const charStart = previousCharStart + readVarUint(bytes, cursor);
    const charLength = readVarUint(bytes, cursor);
    const rect: [number, number, number, number] = [
      previousRect[0] + zigZagDecode(readVarUint(bytes, cursor)),
      previousRect[1] + zigZagDecode(readVarUint(bytes, cursor)),
      previousRect[2] + zigZagDecode(readVarUint(bytes, cursor)),
      previousRect[3] + zigZagDecode(readVarUint(bytes, cursor)),
    ];
    records.push({
      chars: [charStart, charStart + charLength],
      rect: [
        rect[0] / geometry.precision,
        rect[1] / geometry.precision,
        rect[2] / geometry.precision,
        rect[3] / geometry.precision,
      ],
    });
    previousCharStart = charStart;
    previousRect = rect;
  }

  if (cursor.value !== bytes.length) {
    throw DocPackError.make({
      code: 'invalid-geometry',
      message: 'Geometry stream has trailing bytes',
    });
  }

  return records;
}
